// Город, район и метро по пину анкеты (26.10.2026) — одна функция для формы и
// сервера, без внешних API.
//
// ЧТО БЫЛО. Район брался «ближайшим центром» из 12 районов Баку, а контур Баку
// нарочно захватывал Хырдалан. Итог в обращениях репетиторов: «Xocəsən» у
// анкет из Xırdalan и Masazır (10 штук правили руками), «Elmlər Akademiyası» у
// Lökbatan, метро «Qara Qarayev» у Sabunçu qəsəbəsi.
//
// КАК ТЕПЕРЬ (решение владельца 26.10.2026):
//   1. Район — по попаданию точки в границы (src/data/az-places.json, из
//      OpenStreetMap): 12 районов Баку → city baku + код района;
//      Абшеронский район → city absheron + ближайший посёлок (Xırdalan,
//      Masazır…); Сумгаит → city sumgait без района.
//   2. Вне всех границ — район пустой, город — ближайший центр map_cities.
//   3. Метро — только если ближайшая станция не дальше METRO_MAX_KM по прямой.
// Ручной выбор в форме важнее: сервер принимает присланный район или метро,
// если код подходит этому городу (isDistrictOf, isMetro).
//
// Без серверных импортов: модуль читают форма, сервер, отчёт по данным
// (scripts/report-district-mismatch.mts) и scripts/verify-tutor-places.mts.

import places from "@/data/az-places.json";
import { BAKU_CITY, DISTRICTS, METRO, labelOf, type Lang3, type Metro, type TaxItem } from "@/data/map-taxonomy";
import type { MapCity } from "@/lib/map-cities";

type Ring = [number, number][];
const DATA = places as unknown as {
  source: string;
  districts: Record<string, Ring[]>;
  absheron: Ring[];
  sumgait: Ring[];
  absheron_places: (TaxItem & { lat: number; lng: number })[];
};

/** Метро дальше этого (км по прямой) — не ставим (решение владельца 26.10.2026). */
export const METRO_MAX_KM = 1.5;
export const ABSHERON_CITY = "absheron";
export const SUMGAIT_CITY = "sumgait";
/** Посёлки Абшеронского района — подпись «района» у анкет с city = absheron. */
export const ABSHERON_PLACES: (TaxItem & { lat: number; lng: number })[] = DATA.absheron_places;
/** Подписи районов и посёлков — для показа кода из map_partners.district. */
export const DISTRICT_LABELS: TaxItem[] = [...DISTRICTS, ...ABSHERON_PLACES];
export const districtLabel = (code: string, lang: Lang3) => labelOf(DISTRICT_LABELS, code, lang);
/** Ссылка на источник границ — показывается в форме под строкой «Rayon: …». */
export const PLACES_SOURCE = DATA.source;

// Кольца — внешние куски района (у Абшерона их два); точка внутри района,
// если она внутри любого куска. Чёт-нечет по всем кольцам сразу превратил бы
// кусок внутри другого куска в дыру.
function inRing(r: Ring, lat: number, lng: number): boolean {
  let c = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [yi, xi] = r[i];
    const [yj, xj] = r[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) c = !c;
  }
  return c;
}
const inRings = (rings: Ring[], lat: number, lng: number) => rings.some((r) => inRing(r, lat, lng));

/** Расстояние по прямой, км (плоская формула с поправкой долготы — для десятков км хватает). */
export function km(a: { lat: number; lng: number }, lat: number, lng: number): number {
  const k = Math.cos((lat * Math.PI) / 180);
  return Math.sqrt((a.lat - lat) ** 2 + ((a.lng - lng) * k) ** 2) * 111.32;
}

function nearest<T extends { lat: number; lng: number }>(list: readonly T[], lat: number, lng: number): T | null {
  let best: T | null = null;
  let d = Infinity;
  for (const x of list) {
    const dd = km(x, lat, lng);
    if (dd < d) {
      d = dd;
      best = x;
    }
  }
  return best;
}

/** Станция метро не дальше METRO_MAX_KM; дальше — null. */
export function metroNear(lat: number, lng: number): Metro | null {
  const m = nearest(METRO, lat, lng);
  return m && km(m, lat, lng) <= METRO_MAX_KM ? m : null;
}

export type PinPlace = { city: string | null; district: string | null; metro: string | null };

/**
 * Город, район (или посёлок Абшерона) и метро по точке. cities — справочник
 * map_cities: вне границ город — ближайший действующий центр, кроме Баку
 * (точка на краю Сумгаита или Абшерона — щель между границами — уходит к ним).
 */
export function placeByPin(lat: number, lng: number, cities: readonly MapCity[]): PinPlace {
  for (const [code, rings] of Object.entries(DATA.districts)) {
    if (inRings(rings, lat, lng)) return { city: BAKU_CITY, district: code, metro: metroNear(lat, lng)?.code ?? null };
  }
  if (inRings(DATA.absheron, lat, lng)) {
    return { city: ABSHERON_CITY, district: nearest(ABSHERON_PLACES, lat, lng)?.code ?? null, metro: metroNear(lat, lng)?.code ?? null };
  }
  if (inRings(DATA.sumgait, lat, lng)) return { city: SUMGAIT_CITY, district: null, metro: null };
  const others = cities.filter((c) => c.is_active && c.code !== BAKU_CITY);
  return { city: nearest(others, lat, lng)?.code ?? null, district: null, metro: null };
}

/** Код района подходит городу: районы Баку — у baku, посёлки — у absheron. */
export function isDistrictOf(city: string | null, code: string | null | undefined): boolean {
  if (!code) return false;
  if (city === BAKU_CITY) return DISTRICTS.some((d) => d.code === code);
  if (city === ABSHERON_CITY) return ABSHERON_PLACES.some((d) => d.code === code);
  return false;
}

/** Районы, из которых выбирают вручную («Dəyiş»), — по городу пина. */
export function districtChoices(city: string | null): TaxItem[] {
  if (city === BAKU_CITY) return DISTRICTS;
  if (city === ABSHERON_CITY) return ABSHERON_PLACES;
  return [];
}

export const isMetro = (code: string | null | undefined) => !!code && METRO.some((m) => m.code === code);

// ── Поиск города в форме (26.10.2026) ─────────────────────────────────────
// Владелец на iPhone: в списке нет Xırdalan, список длинный. Поле с поиском по
// буквам: города map_cities + посёлки Абшерона (Xırdalan, Masazır…) и Баку
// (Mərdəkan, Buzovna…) из az-places.json. Посёлок — это выбор места на карте:
// город анкеты у него — baku / absheron, район всё равно считает пин.

type Settlement = TaxItem & { lat: number; lng: number; city: string; district: string | null };
const SETTLEMENTS: Settlement[] = (DATA as unknown as { settlements?: Settlement[] }).settlements ?? [];

export type PlaceOption = {
  /** Код map_cities или код посёлка (abs_*, bak_*). */
  key: string;
  /** Код map_cities, который получает анкета без пина. */
  city: string;
  lat: number;
  lng: number;
  /** Масштаб карты после выбора: город — 13, посёлок — 14. */
  zoom: number;
  label: Record<Lang3, string>;
  /** Мелкая подпись: «Abşeron», «Bakı · Xəzər»; у городов пусто. */
  hint: Record<Lang3, string> | null;
};

/** Сверху без поиска — самые частые (решение владельца 26.10.2026). */
export const TOP_PLACE_KEYS = [BAKU_CITY, SUMGAIT_CITY, "abs_xirdalan", "ganja"];

export function placeOptions(cities: readonly MapCity[]): PlaceOption[] {
  const byCode = new Map(cities.map((c) => [c.code, c]));
  const nm = (code: string): Record<Lang3, string> | null => {
    const c = byCode.get(code);
    return c ? { az: c.name_az, ru: c.name_ru, en: c.name_en } : null;
  };
  const baku = nm(BAKU_CITY) ?? { az: "Bakı", ru: "Баку", en: "Baku" };
  const absh = nm(ABSHERON_CITY) ?? { az: "Abşeron", ru: "Абшерон", en: "Absheron" };
  const out: PlaceOption[] = cities
    .filter((c) => c.is_active)
    .map((c) => ({ key: c.code, city: c.code, lat: c.lat, lng: c.lng, zoom: 13, label: { az: c.name_az, ru: c.name_ru, en: c.name_en }, hint: null }));
  for (const p of ABSHERON_PLACES) out.push({ key: p.code, city: ABSHERON_CITY, lat: p.lat, lng: p.lng, zoom: 14, label: { az: p.az, ru: p.ru, en: p.en }, hint: absh });
  for (const p of SETTLEMENTS) {
    const d = p.district ? DISTRICTS.find((x) => x.code === p.district) : null;
    const hint = p.city === BAKU_CITY ? (d ? { az: `${baku.az} · ${d.az}`, ru: `${baku.ru} · ${d.ru}`, en: `${baku.en} · ${d.en}` } : baku) : nm(p.city);
    out.push({ key: p.code, city: p.city, lat: p.lat, lng: p.lng, zoom: 14, label: { az: p.az, ru: p.ru, en: p.en }, hint });
  }
  return out;
}

/** Поиск по буквам (ə=e, ı=i, ş=s…): сначала совпадение с начала слова, потом внутри. */
export function searchPlaces(options: readonly PlaceOption[], query: string, fold: (s: string) => string, limit = 12): PlaceOption[] {
  const q = fold(query);
  if (!q) return TOP_PLACE_KEYS.map((k) => options.find((o) => o.key === k)).filter((o): o is PlaceOption => !!o);
  const scored: { o: PlaceOption; s: number }[] = [];
  for (const o of options) {
    const names = [o.label.az, o.label.ru, o.label.en].map(fold);
    const s = names.some((n) => n.startsWith(q)) ? 0 : names.some((n) => n.split(/[\s-]+/).some((w) => w.startsWith(q))) ? 1 : names.some((n) => n.includes(q)) ? 2 : -1;
    if (s >= 0) scored.push({ o, s: s * 10 + (o.hint ? 1 : 0) });
  }
  return scored.sort((a, b) => a.s - b.s || a.o.label.az.localeCompare(b.o.label.az, "az")).slice(0, limit).map((x) => x.o);
}
